// ============ IA ennemie "maligne" ============
// Choisit la meilleure compétence selon le contexte de combat (PV, cooldowns, état du joueur),
// au lieu de spammer une attaque de base. Partagé entre le combat solo et le combat coop.
//
// État IA (cooldowns + compteur de tours) : à stocker dans un ref (solo) ou dans le game_state (coop).
export const newEnemyAIState = () => ({ cooldowns: {}, turnCount: 0 });

// Décrémente tous les cooldowns d'un cran et incrémente le compteur de tours.
// Appelé au DÉBUT de chaque tour ennemi (même s'il est étourdi : ses sorts récupèrent).
export const tickEnemyCooldowns = (state) => {
  const prev = state || newEnemyAIState();
  const cooldowns = {};
  for (const [id, v] of Object.entries(prev.cooldowns || {})) {
    if (v - 1 > 0) cooldowns[id] = v - 1;
  }
  return { cooldowns, turnCount: (prev.turnCount || 0) + 1 };
};

// Choisit une compétence (ou null = attaque de base).
// ctx : { enemyHpFrac, playerHpFrac, turnCount, cooldowns, playerEffects, mode }
//   - playerEffects : effets actuels DU JOUEUR (solo) — facultatif en coop
//   - mode : "solo" | "coop" (en coop, les compétences purement debuff sont ignorées
//            car le combat coop ne gère pas encore les effets d'état sur les joueurs)
export const chooseEnemyAbility = (enemy, ctx = {}) => {
  const abilities = enemy?.competences || [];
  if (abilities.length === 0) return null;
  const {
    enemyHpFrac = 1,
    playerHpFrac = 1,
    turnCount = 1,
    cooldowns = {},
    playerEffects = null,
    mode = "solo",
  } = ctx;

  const available = abilities.filter((a) => (cooldowns[a.id] || 0) <= 0);
  if (available.length === 0) return null;

  const pBuffs = playerEffects?.combatBuffs || {};
  const playerBurning = (playerEffects?.brulure?.duration || 0) > 0;
  const playerStunned =
    (playerEffects?.stun?.duration || 0) > 0 || (playerEffects?.gel?.duration || 0) > 0;

  let best = null;
  let bestScore = 48; // seuil = équivalent d'une attaque de base

  for (const a of available) {
    let score = 0;

    if (a.type === "soin") {
      // Ne se soigne JAMAIS en pleine forme ; d'autant plus prioritaire que les PV sont bas.
      if (enemyHpFrac > 0.6) score = -100;
      else score = 50 + (1 - enemyHpFrac) * 80; // PV à 15% → ~118 (très prioritaire)
    } else if (a.type === "debuff") {
      if (mode === "coop") {
        score = -100; // pas de système d'effets joueur en coop
      } else {
        score = 52;
        if (turnCount <= 2) score += 30; // ouvrir le combat en posant ses debuffs
        const stun = a.applyOnPlayer?.stun;
        if (stun) {
          if (playerStunned) score = -100; // inutile de re-stun
          else score += 26; // le contrôle est très fort
        }
        const cb = a.applyOnPlayer?.combatBuff;
        if (cb && !Array.isArray(cb) && (pBuffs[cb.stat] || 0) < 0) score -= 25; // déjà affaibli
      }
    } else {
      // attaque
      const mult = a.multiplier || 1;
      score = 50 + (mult - 1) * 22; // les gros multiplicateurs restent attractifs, sans tout écraser
      if (playerHpFrac < 0.35) score += 45; // achever un joueur affaibli (priorité au burst)
      if (mode !== "coop" && a.applyOnPlayer?.stun && !playerStunned) score += 18;
      if (a.applyOnPlayer?.brulure) score += playerBurning ? 2 : 16; // brûlure utile si pas déjà en feu
    }

    score += Math.random() * 12 - 6; // un brin d'aléatoire pour varier
    if (score > bestScore) {
      bestScore = score;
      best = a;
    }
  }

  return best; // null si rien ne bat l'attaque de base
};
